import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, Target, Clock, TrendingUp, Plus, RefreshCw, Activity, Gauge } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadialBarChart, RadialBar, Cell } from 'recharts';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const today = new Date();
      let startDate, endDate;

      if (period === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (period === 'month') {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else {
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      }

      const response = await api.get('/analytics/dashboard', {
        params: { start_date: startDate, end_date: endDate }
      });
      setData(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await api.post('/seed-data');
      toast.success('Master data seeded successfully!');
      fetchDashboard();
    } catch (error) {
      console.error('Seed error:', error);
    }
  };

  useEffect(() => {
    seedData();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [period]);

  const kpis = data?.kpis || {
    total_produced: 0, total_rejected: 0, total_target: 0,
    rejection_rate: 0, target_achievement: 0, total_downtime: 0, total_runs: 0
  };

  const oee = data?.oee || { overall: 0, availability: 0, performance: 0, quality: 0 };

  const getOEEColor = (value) => {
    if (value >= 85) return '#00875A';
    if (value >= 60) return '#FFAB00';
    return '#DE350B';
  };

  const getOEELabel = (value) => {
    if (value >= 85) return 'World Class';
    if (value >= 60) return 'Good';
    if (value >= 40) return 'Typical';
    return 'Low';
  };

  // Prepare OEE gauge data
  const oeeGaugeData = [
    { name: 'OEE', value: oee.overall, fill: getOEEColor(oee.overall) }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Production overview and OEE metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchDashboard} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {user?.role === 'SUPERVISOR' && (
            <Button onClick={() => navigate('/production-runs/new')} data-testid="new-run-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Production Run
            </Button>
          )}
        </div>
      </div>

      {/* OEE Overview Card */}
      <Card className="border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800" data-testid="oee-overview-card">
        <CardContent className="py-6">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* OEE Gauge */}
            <div className="flex flex-col items-center">
              <div className="relative w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={oeeGaugeData}>
                    <RadialBar dataKey="value" cornerRadius={10} background={{ fill: 'rgba(255,255,255,0.1)' }} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-5xl font-mono font-bold text-white">{oee.overall}</span>
                  <span className="text-lg text-slate-300">%</span>
                </div>
              </div>
              <div className="text-center mt-2">
                <p className="text-white font-semibold text-lg">Overall OEE</p>
                <p className={`text-sm font-medium`} style={{ color: getOEEColor(oee.overall) }}>{getOEELabel(oee.overall)}</p>
              </div>
            </div>

            {/* OEE Components */}
            <div className="flex-1 grid grid-cols-3 gap-6">
              <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
                <p className="text-slate-300 text-sm font-medium uppercase tracking-wide">Availability</p>
                <p className="text-4xl font-mono font-bold text-white mt-2">{oee.availability}<span className="text-xl">%</span></p>
                <p className="text-xs text-slate-400 mt-1">Run Time / Planned Time</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
                <p className="text-slate-300 text-sm font-medium uppercase tracking-wide">Performance</p>
                <p className="text-4xl font-mono font-bold text-white mt-2">{oee.performance}<span className="text-xl">%</span></p>
                <p className="text-xs text-slate-400 mt-1">Actual / Target Output</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
                <p className="text-slate-300 text-sm font-medium uppercase tracking-wide">Quality</p>
                <p className="text-4xl font-mono font-bold text-white mt-2">{oee.quality}<span className="text-xl">%</span></p>
                <p className="text-xs text-slate-400 mt-1">Good / Total Count</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200" data-testid="kpi-produced">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Total Produced</p>
                <p className="kpi-value text-slate-900 mt-2">{kpis.total_produced.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mt-1">of {kpis.total_target.toLocaleString()} target</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-100">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="kpi-rejected">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Rejection Rate</p>
                <p className={`kpi-value mt-2 ${kpis.rejection_rate > 5 ? 'text-red-600' : kpis.rejection_rate > 2 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {kpis.rejection_rate.toFixed(1)}%
                </p>
                <p className="text-sm text-slate-500 mt-1">{kpis.total_rejected.toLocaleString()} rejected</p>
              </div>
              <div className="p-3 rounded-lg bg-red-100">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="kpi-achievement">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Target Achievement</p>
                <p className={`kpi-value mt-2 ${kpis.target_achievement >= 95 ? 'text-green-600' : kpis.target_achievement >= 85 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {kpis.target_achievement.toFixed(1)}%
                </p>
                <p className="text-sm text-slate-500 mt-1">{kpis.total_runs} production runs</p>
              </div>
              <div className="p-3 rounded-lg bg-green-100">
                <Target className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="kpi-downtime">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="kpi-label">Total Downtime</p>
                <p className="kpi-value text-slate-900 mt-2">{kpis.total_downtime}</p>
                <p className="text-sm text-slate-500 mt-1">minutes</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* OEE Trend Chart */}
      {data?.oee_trend && data.oee_trend.length > 0 && (
        <Card className="border-slate-200" data-testid="oee-trend-chart">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary" />
              OEE Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.oee_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', fontFamily: 'JetBrains Mono' }} />
                  <Legend />
                  <Line type="monotone" dataKey="oee" stroke="#0052CC" strokeWidth={3} dot={{ fill: '#0052CC', strokeWidth: 2 }} name="OEE %" />
                  <Line type="monotone" dataKey="availability" stroke="#00875A" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Availability" />
                  <Line type="monotone" dataKey="performance" stroke="#FFAB00" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Performance" />
                  <Line type="monotone" dataKey="quality" stroke="#DE350B" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Quality" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production Trend */}
        <Card className="border-slate-200" data-testid="production-trend-chart">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Production Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.production_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', fontFamily: 'JetBrains Mono' }} />
                  <Legend />
                  <Line type="monotone" dataKey="produced" stroke="#0052CC" strokeWidth={2} dot={{ fill: '#0052CC', strokeWidth: 2 }} name="Produced" />
                  <Line type="monotone" dataKey="target" stroke="#00875A" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Machine OEE Comparison */}
        <Card className="border-slate-200" data-testid="machine-oee-chart">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Machine OEE Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.oee_by_machine || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="machine" tick={{ fontSize: 10 }} width={60} />
                  <Tooltip contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: 12 }} formatter={(value) => `${value}%`} />
                  <Legend />
                  <Bar dataKey="oee" name="OEE %" radius={[0, 4, 4, 0]}>
                    {(data?.oee_by_machine || []).map((entry, index) => (
                      <Cell key={index} fill={getOEEColor(entry.oee)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Summary */}
      {data?.status_counts && Object.keys(data.status_counts).length > 0 && (
        <Card className="border-slate-200" data-testid="status-summary">
          <CardHeader>
            <CardTitle className="font-heading">Run Status Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(data.status_counts).map(([status, count]) => (
                <div key={status} className={`px-4 py-2 rounded-lg font-medium status-${status.toLowerCase()}`}>
                  {status}: <span className="font-mono">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && kpis.total_runs === 0 && (
        <Card className="border-slate-200 border-dashed" data-testid="empty-state">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="font-heading text-lg font-semibold text-slate-700 mb-2">No Production Data Yet</h3>
            <p className="text-slate-500 mb-4">Start by creating your first production run to see OEE analytics.</p>
            {user?.role === 'SUPERVISOR' && (
              <Button onClick={() => navigate('/production-runs/new')}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Production Run
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
